import type { CondominiumData, MaintenanceSummary } from '@/types/condominium';
import type {
  CondominiumConfigurationPayload,
  CondominiumConfigurationResponse,
} from '@/services/api/establishments.service';

const emptySummary: MaintenanceSummary = {
  total: 0,
  enabledCount: 0,
  monthly: 0,
  quarterly: 0,
  half_yearly: 0,
  annual: 0,
};

/**
 * Allowlist de campos que o backend aceita em cada sub-objeto.
 *
 * Defesa em profundidade contra propriedades extras vazadas pelo persist do
 * useQuizStore (ou por futuras adições de UI que não tenham contrapartida no
 * DTO). O ValidationPipe global tem `forbidNonWhitelisted: true` — qualquer
 * campo desconhecido vira 400.
 */
const SYNDIC_ALLOWED_KEYS = [
  'name',
  'phone',
  'email',
  'address',
  'type',
  'apartment',
  'cpf',
  'cnpj',
  'companyName',
] as const;

const SUB_SYNDIC_ALLOWED_KEYS = [
  'name',
  'phone',
  'email',
  'address',
  'apartment',
  'cpf',
] as const;

const JANITOR_ALLOWED_KEYS = [
  'name',
  'phone',
  'secondaryPhone',
  'email',
  'address',
  'type',
  'apartment',
  'cpf',
  'cnpj',
] as const;

const ADMINISTRATOR_ALLOWED_KEYS = [
  'name',
  'contact',
  'phone',
  'email',
  'cnpj',
] as const;

function pickAllowed<T extends Record<string, unknown>, K extends string>(
  obj: T | undefined | null,
  keys: readonly K[],
): Pick<T, Extract<keyof T, K>> | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const out = {} as Pick<T, Extract<keyof T, K>>;
  let hasAny = false;
  for (const k of keys) {
    const key = k as unknown as keyof T;
    if (key in obj && obj[key] !== undefined) {
      out[key as Extract<keyof T, K>] = obj[key] as T[Extract<keyof T, K>];
      hasAny = true;
    }
  }
  return hasAny ? out : undefined;
}

function addBoolean(target: Record<string, unknown>, key: string, value: boolean | undefined | null) {
  if (value === undefined || value === null) return;
  target[key] = value;
}

function addNumber(target: Record<string, unknown>, key: string, value: number | undefined | null) {
  if (value === undefined || value === null) return;
  target[key] = value;
}

function sanitizeInfrastructure(
  infrastructure: CondominiumData['infrastructure'] | undefined,
): CondominiumConfigurationPayload['infrastructure'] {
  if (!infrastructure) {
    return undefined;
  }

  const sanitized: Record<string, unknown> = {};

  addBoolean(sanitized, 'apartmentsWithBBQ', infrastructure.apartmentsWithBBQ);
  addBoolean(sanitized, 'apartmentsWithBalcony', infrastructure.apartmentsWithBalcony);
  addNumber(sanitized, 'partyHalls', infrastructure.partyHalls);
  addBoolean(sanitized, 'gym', infrastructure.gym);
  addNumber(sanitized, 'gymQuantity', infrastructure.gymQuantity);
  addNumber(sanitized, 'pools', infrastructure.pools);
  addBoolean(sanitized, 'gasSystem', infrastructure.gasSystem);

  if (infrastructure.saunas?.exists !== undefined) {
    sanitized['saunas'] = {
      exists: Boolean(infrastructure.saunas.exists),
      ...(infrastructure.saunas.quantity ? { quantity: infrastructure.saunas.quantity } : {}),
      ...(infrastructure.saunas.maintenanceCompany
        ? { maintenanceCompany: infrastructure.saunas.maintenanceCompany }
        : {}),
    };
  }

  const sportsCourts = (infrastructure.sportsCourts ?? []).filter(
    (court) => court && court.exists !== undefined,
  );
  if (sportsCourts.length > 0) {
    sanitized['sportsCourts'] = sportsCourts.map((court) => ({
      exists: Boolean(court.exists),
      ...(court.type ? { type: court.type } : {}),
      ...(court.quantity ? { quantity: court.quantity } : {}),
    }));
  }

  addBoolean(sanitized, 'playground', infrastructure.playground);

  if (infrastructure.security24h) {
    sanitized['security24h'] = {
      exists: Boolean(infrastructure.security24h.exists),
      company: infrastructure.security24h.company,
    };
  }

  if (infrastructure.remotePorter) {
    sanitized['remotePorter'] = {
      exists: Boolean(infrastructure.remotePorter.exists),
      company: infrastructure.remotePorter.company,
    };
  }

  addNumber(sanitized, 'totalCameras', infrastructure.totalCameras);
  addNumber(sanitized, 'elevators', infrastructure.elevators);

  addBoolean(sanitized, 'hasStairs', infrastructure.hasStairs);
  addBoolean(sanitized, 'externalStairs', infrastructure.externalStairs);
  addBoolean(sanitized, 'internalStairs', infrastructure.internalStairs);
  addBoolean(sanitized, 'pressurizedStaircase', infrastructure.pressurizedStaircase);

  if (infrastructure.electricGenerator?.exists !== undefined) {
    sanitized['electricGenerator'] = {
      exists: Boolean(infrastructure.electricGenerator.exists),
      ...(infrastructure.electricGenerator.quantity
        ? { quantity: infrastructure.electricGenerator.quantity }
        : {}),
    };
  }

  if (infrastructure.electricSubstation?.exists !== undefined) {
    sanitized['electricSubstation'] = {
      exists: Boolean(infrastructure.electricSubstation.exists),
      ...(infrastructure.electricSubstation.quantity
        ? { quantity: infrastructure.electricSubstation.quantity }
        : {}),
    };
  }

  addBoolean(sanitized, 'electricPumps', infrastructure.electricPumps);
  addNumber(sanitized, 'parkingSpaces', infrastructure.parkingSpaces);
  addBoolean(sanitized, 'lightningRodSystem', infrastructure.lightningRodSystem);
  addBoolean(sanitized, 'roofingSystem', infrastructure.roofingSystem);
  addBoolean(sanitized, 'hotWaterSystem', infrastructure.hotWaterSystem);
  addBoolean(sanitized, 'airConditioningSystem', infrastructure.airConditioningSystem);
  addBoolean(sanitized, 'alarmSystem', infrastructure.alarmSystem);

  if (infrastructure.garden) {
    sanitized['garden'] = {
      exists: Boolean(infrastructure.garden.exists),
      maintenanceCompany: infrastructure.garden.maintenanceCompany,
    };
  }

  if (infrastructure.fireDoors?.exists !== undefined) {
    sanitized['fireDoors'] = {
      exists: Boolean(infrastructure.fireDoors.exists),
      ...(infrastructure.fireDoors.quantity ? { quantity: infrastructure.fireDoors.quantity } : {}),
    };
  }

  if (infrastructure.electronicGates?.exists !== undefined) {
    sanitized['electronicGates'] = {
      exists: Boolean(infrastructure.electronicGates.exists),
      ...(infrastructure.electronicGates.quantity
        ? { quantity: infrastructure.electronicGates.quantity }
        : {}),
    };
  }

  addBoolean(sanitized, 'electricVehicleChargers', infrastructure.electricVehicleChargers);

  return sanitized;
}

export function mapStoreToConfigurationPayload(
  data: CondominiumData,
): CondominiumConfigurationPayload {
  return {
    name: data.name,
    document: data.document,
    address: data.address,
    neighborhood: data.neighborhood,
    city: data.city,
    state: data.state,
    cep: data.cep,
    contactPhone: data.contactPhone,
    contactEmail: data.contactEmail,
    website: data.website,
    foundationDate: data.foundationDate,
    type: data.type,
    totalApartments: data.totalApartments,
    totalCommercialUnits: data.totalCommercialUnits,
    totalPublicAgencies: data.totalPublicAgencies,
    totalFloors: data.totalFloors,
    totalGarageFloors: data.totalGarageFloors,
    syndic: pickAllowed(
      data.syndic as Record<string, unknown> | undefined,
      SYNDIC_ALLOWED_KEYS,
    ) as CondominiumConfigurationPayload['syndic'],
    subSyndic: pickAllowed(
      data.subSyndic as Record<string, unknown> | undefined,
      SUB_SYNDIC_ALLOWED_KEYS,
    ) as CondominiumConfigurationPayload['subSyndic'],
    janitor: pickAllowed(
      data.janitor as Record<string, unknown> | undefined,
      JANITOR_ALLOWED_KEYS,
    ) as CondominiumConfigurationPayload['janitor'],
    administrator: pickAllowed(
      data.administrator as Record<string, unknown> | undefined,
      ADMINISTRATOR_ALLOWED_KEYS,
    ) as CondominiumConfigurationPayload['administrator'],
    securityCompany: data.securityCompany,
    cleaningCompany: data.cleaningCompany,
    erpSystem: data.erpSystem,
    elevatorCompany: data.elevatorCompany,
    infrastructure: sanitizeInfrastructure(data.infrastructure),
    // IMPORTANTE: Só incluir maintenanceSetup se tiver items
    // Se items estiver vazio, NÃO enviar para não sobrescrever dados existentes no banco
    maintenanceSetup:
      data.maintenanceSetup && data.maintenanceSetup.items?.length > 0
        ? {
            startDate: data.maintenanceSetup.startDate,
            items: data.maintenanceSetup.items,
            summary: data.maintenanceSetup.summary,
          }
        : undefined,
  };
}

export function mapConfigurationResponseToStore(
  response: CondominiumConfigurationResponse,
): CondominiumData {
  const maintenanceSetup = response.maintenanceSetup
    ? {
        startDate: response.maintenanceSetup.startDate,
        items: response.maintenanceSetup.items ?? [],
        summary: response.maintenanceSetup.summary ?? emptySummary,
      }
    : undefined;

  return {
    name: response.name ?? '',
    document: response.document,
    address: response.address ?? '',
    cep: response.cep ?? '',
    neighborhood: response.neighborhood,
    city: response.city,
    state: response.state,
    foundationDate: response.foundationDate,
    contactPhone: response.contactPhone,
    contactEmail: response.contactEmail,
    website: response.website,
    type: response.type,
    totalApartments: response.totalApartments,
    totalCommercialUnits: response.totalCommercialUnits,
    totalPublicAgencies: response.totalPublicAgencies,
    totalFloors: response.totalFloors,
    totalGarageFloors: response.totalGarageFloors,
    syndic: response.syndic,
    syndicIsPrefilledByAdmin: response.syndicIsPrefilledByAdmin,
    subSyndic: response.subSyndic,
    janitor: response.janitor,
    administrator: response.administrator,
    securityCompany: response.securityCompany,
    cleaningCompany: response.cleaningCompany,
    erpSystem: response.erpSystem,
    elevatorCompany: response.elevatorCompany,
    infrastructure: response.infrastructure as CondominiumData['infrastructure'],
    maintenanceSetup,
  };
}
